import { gql } from '@apollo/client';

export const GET_PORTFOLIOS_QUERY = gql`
  query Portfolios($start: Int, $limit: Int, $sort: [SortParam], $filter: FilterParam) {
    portfolios(start: $start, limit: $limit, sort: $sort, filter: $filter) {
      byIsinUpload
      createdDate
      description
      id
      portfolioType
      isDedicated
      activity
      itemsTypeCount {
        issuers
        issues
      }
      modifiedDate
      name
      noOfItems {
        entities
        instruments
        funds
        ratingNews
        researches
      }
      watchlistEnabled
      coverage {
        creditReview
        creditRating
        esg
      }
    }
    total
  }
`;

export const GET_PORTFOLIO_QUERY = gql`
  query Portfolio($id: ObjectIdType) {
    portfolio(id: $id) {
      byIsinUpload
      createdDate
      description
      id
      portfolioType
      isDedicated
      activity
      itemsTypeCount {
        issuers
        issues
      }
      modifiedDate
      name
      noOfItems {
        funds
        entities
        instruments
        ratingNews
        researches
        events
        uploads
      }
      watchlistEnabled
      coverage {
        creditReview
        creditRating
        esg
      }
      statistics {
        regions {
          name
          count
        }
        subregions {
          name
          count
        }
        industries {
          name
          count
        }
      }
      summary {
        lastAdded {
          artifactId
          addedDate
          type
          name
          country
          isin
        }
        latestNews {
          artifactId
          title
          type
          publicationDate
        }
      }
    }
  }
`;

export const GET_ENTITIES_QUERY = gql`
  query PortfolioItems(
    $portfolioId: ObjectIdType
    $start: Int
    $limit: Int
    $sort: [SortParam]
    $requestedTab: RequestedEntitiesTab
    $filter: EntitiesFilter
  ) {
    totalPortfolioItems(portfolioId: $portfolioId, requestedTab: $requestedTab, filter: $filter)
    portfolioItems(
      portfolioId: $portfolioId
      start: $start
      limit: $limit
      sort: $sort
      requestedTab: $requestedTab
      filter: $filter
    ) {
      module
      name
      artifactId
      addedDate
      id
      sector
      region
      subregion
      industry
      countryCode
      reportedWeight
      meta {
        demandType
        storefrontHasLicence
        storefrontPurchased
        storefrontOldScore
        storefrontRatingType
        storefrontRatingOutlook
        storefrontScoreUpdate
        esgPurchased
        esgRequestState
        esgCustomScore
        esgOldTotalScore
        esgScoreUpdate
        esgSentifiHasScore
        esgSentifiHasCompany
        esgSentifiHasPermission
        esgSentifiRequestState
        sigRequested
        sigOldScore
        sigScoreUpdate
        sigMonitoring
        sigPOD
        sigPODDefinition
        sigCountryRiskAssessment
        artifactIdType
        storefrontSubscriptionType
        sigSubscriptionType
        esgSubscriptionType
        sigCity
        sigPostcode
        sigAddress
      }
      scores {
        creditReview
        creditRating
        creditRatingShortTermRating
        esgTotal
        esgEnvironment
        esgSocial
        esgGovernance
        sentifi {
          esgScore
          eScore
          sScore
          gScore
          status
        }
        cstOrderly {
          climateLoss
          physicalRiskLoss
          transitionRiskLoss
        }
        cstDisorderly {
          climateLoss
          physicalRiskLoss
          transitionRiskLoss
        }
        cstHotHouse {
          climateLoss
          physicalRiskLoss
          transitionRiskLoss
        }
      }
      activity {
        esg
        creditReview
        creditRating
      }
    }
  }
`;

export const GET_ENTITIES_QUERY_BY_ARTIFACT_ID = gql`
  query PortfolioItems($artifactIds: [String], $start: Int, $limit: Int, $sort: [SortParam]) {
    totalPortfolioItems(portfolioId: $portfolioId, requestedTab: $requestedTab, filter: $filter)
    portfolioItems(artifactIds: $artifactIds, start: $start, limit: $limit, sort: $sort) {
      module
      name
      addedDate
      id
      sector
      region
      subregion
      industry
      countryCode
      reportedWeight
      meta {
        demandType
        storefrontHasLicence
        storefrontPurchased
        storefrontOldScore
        storefrontScoreUpdate
        esgPurchased
        esgRequestState
        esgCustomScore
        esgOldTotalScore
        esgScoreUpdate
        sigRequested
        sigOldScore
        sigScoreUpdate
        sigMonitoring
        sigPOD
        sigPODDefinition
        sigCountryRiskAssessment
        artifactIdType
      }
      scores {
        creditReview
        creditRating
        esgTotal
        esgEnvironment
        esgSocial
        esgGovernance
      }
      activity {
        esg
        creditReview
        creditRating
      }
    }
  }
`;

export const GET_ENTITY_QUERY = gql`
  query PortfolioItem($id: String) {
    portfolioItem(id: $id) {
      module
      name
      artifactId
      addedDate
      id
      sector
      region
      subregion
      industry
      countryCode
      reportedWeight
      meta {
        demandType
        storefrontHasLicence
        storefrontPurchased
        esgPurchased
        esgRequestState
        esgCustomScore
        sigRequested
        sigOldScore
        sigScoreUpdate
        sigMonitoring
        sigPOD
        sigPODDefinition
        sigCountryRiskAssessment
        artifactIdType
      }
      scores {
        creditReview
        creditRating
        esgTotal
        esgEnvironment
        esgSocial
        esgGovernance
      }
      activity {
        esg
        creditReview
        creditRating
      }
    }
  }
`;

export const GET_INSTRUMENTS_QUERY = gql`
  query PortfolioInstruments(
    $portfolioId: ObjectIdType
    $start: Int
    $limit: Int
    $sort: [SortParam]
    $filter: InstrumentsFilter
  ) {
    totalInstruments(portfolioId: $portfolioId, filter: $filter)
    instruments(
      portfolioId: $portfolioId
      start: $start
      limit: $limit
      sort: $sort
      filter: $filter
    ) {
      id
      name
      date
      volume
      artifactId
      isin
      currencyCode
      entityId
      entityName
      addedDate
      meta {
        demandType
        available
        hasLicence
        rating
        ratingType
        ratingDate
      }
      coupon {
        artifactId
        couponPercent
        maturityDate
        maturityPerpetualFlag
      }
    }
  }
`;

export const GET_FUNDS_QUERY = gql`
  query fund(
    $portfolioId: ObjectIdType
    $start: Int
    $limit: Int
    $sort: [SortParam]
    $filter: FundsFilter
  ) {
    fund(portfolioId: $portfolioId, start: $start, limit: $limit, sort: $sort, filter: $filter) {
      name
      peergroupName
      isin
      rank
      rating
      ratingPoints
      returnValue
      risk
      isAif
      isQualitative
    }
  }
`;

export const GET_RATING_NEWS_QUERY = gql`
  query PortfolioRatingNews(
    $portfolioId: ObjectIdType
    $start: Int
    $limit: Int
    $sort: [SortParam]
  ) {
    ratingNews(portfolioId: $portfolioId, start: $start, limit: $limit, sort: $sort) {
      artifactId
      language
      title
      addedDate
      publicationDate
    }
  }
`;

export const GET_RESEARCH_QUERY = gql`
  query PortfolioResearchQuery(
    $portfolioId: ObjectIdType
    $language: Language
    $start: Int
    $limit: Int
    $sort: [SortParam]
  ) {
    researches(
      portfolioId: $portfolioId
      language: $language
      start: $start
      limit: $limit
      sort: $sort
    ) {
      artifactId
      language
      title
      addedDate
      publicationDate
    }
  }
`;

export const GET_EVENTS_QUERY = gql`
  query PortfolioEvents(
    $portfolioId: ObjectIdType
    $start: Int
    $limit: Int
    $sort: [SortParam]
    $filter: EventFilter
  ) {
    events(portfolioId: $portfolioId, start: $start, limit: $limit, sort: $sort, filter: $filter) {
      id
      artifactId
      title
      eventDate
      addedDate
      category
      industry
      region
      meta {
        externalId
        connectionLink
        userRegistered
        eventType
        eventStatus
      }
    }
  }
`;

export const GET_UPLOADS_QUERY = gql`
  query PortfolioUploads($portfolioId: ObjectIdType, $start: Int, $limit: Int, $sort: [SortParam]) {
    uploadHistory(portfolioId: $portfolioId, start: $start, limit: $limit, sort: $sort) {
      id
      userId
      fileName
      uploadType
      itemsType
      uploadStatus
      createdDate
      portfolioId
      link
      errorsLink
      errorReason
      results {
        total
        instrumentsFound
        totalInstruments
        esgFound
        creditRatingFound
        creditReviewFound
        uploadedItems
        validationErrors
      }
    }
  }
`;
